// @ts-nocheck
import { Box, Grid } from '@mui/material';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import OtherDetails from './OtherDetails/OtherDetails';
import EditValueModal from './Modals/EditValueModal';
import BasicDetails from './BasicDetails/BasicDetails';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  MENTIONED_ENTITIES,
  NOOP,
  NOOP_PROMISE,
} from '../../../const/CommonConst';
import {
  filterSectionsByPermissions,
  isValueEmpty,
} from '../../../utils/Utils';
import ChatBoxPanel from '../ModelInventoryAndAssociation/ChatForum/ChatBoxPanel';
import DraggableErrorSectionDialog from '../DraggableErrorSectionDialog';
import useSectionPermissions from '../../../hooks/ModelInventory/useSectionPermissions.js';
import DerivedAttributeOverrideModal from './DerivedAttributeOverrideModal';
import useWorkflowGroups from '../../../hooks/Configuration/Workflow/useWorkflowGroups';
import { getVisibleAttributes } from '../../../utils/conditionalRuleEvaluator';
import useModelInventory from '../../../hooks/ModelInventory/useModelInventory';
import useAttribute from '../../../hooks/ModelInventory/useAttribute';
import useErrorDialog from '../../../hooks/useErrorDialog';
import {
  applyDateCalendarConstraints,
  applyMandatoryConstraints,
} from '../../../utils/formUtils';
import SectionAccordion from '../Modals/AccordionModal/SectionAccordion.jsx';
import useAIAttributeHighlight from './useAIAttributeHighlight';
import FormSkeleton from '../../Skeleton/FormSkeleton';

const Overview = forwardRef(
  (
    {
      formDetails = EMPTY_OBJECT,
      attributeDetails = EMPTY_OBJECT,
      handleUpdateEntity = NOOP,
      usersList = EMPTY_ARRAY,
      teamUsersList = EMPTY_ARRAY,
      groupList = EMPTY_ARRAY,
      mode = 'edit',
      hasDraftChanges = false,
      entityData = EMPTY_OBJECT,
      entityDetailsFormikForm,
      validateFilteredFormikErrors,
      isMultipleEditMode,
      isUserAuthorizedForUpdateAttribute,
      deleteNote = NOOP,
      editNote = NOOP,
      handleOpenNoteModal = NOOP,
      handleSetAddNoteModalHeading = NOOP,
      handleSetSelectedEntityToAddInEditor = NOOP,
      getAttComments = NOOP_PROMISE,
      allAttributeChats = EMPTY_ARRAY,
      syncAttributes,
      isTableView = true,
      isUserAuthorizedToOverrideAttribute,
      getOverrideAttributeValue = NOOP_PROMISE,
      // Draft Auto-save Props
      handleAutoSave = NOOP,
      hideBasicDetails,
      getAttributeDetails,
      updateInitialValuesFromAPI,
      showErrorsOnSave = false,
      setShowErrorsOnSave = NOOP,
      currentModelStatus = EMPTY_OBJECT,
      entitySelectedGroupId = '',
      onEditButtonStateChange = NOOP,
      onImpactButtonStateChange = NOOP,
      selectedStateNode = null,
      handleClearSelectedState,
      setSelectedStateNode = NOOP,
      setEntitySelectedGroupId = NOOP,
      groupDetails = EMPTY_OBJECT,
      isFromModelOrArtifactsPage = false,
      // formSchema is received as prop and used internally by setFormSchema
      setFormSchema = NOOP,
      isGroupEditModal = false,
      isNodeEdit = false,
      scrollContainerRef,
      descriptiveTitle = 'Descriptive Information',
      preliminarySectionName = 'Preliminary Details',
      groupListForTeamMembers,
      getNextPossibleStates,
      getModelDetails,
      handleGetAttributesData,
      clearDraftValues,
      possibleRoles = EMPTY_ARRAY,
      getCustomTeamDetails,
      associationTypes = EMPTY_OBJECT,
      // AI Autofill Props
      aiSuggestions,
      onAcceptAISuggestion,
      onRejectAISuggestion,
      onUndoAISuggestion,
      getSuggestionForAttribute,
      modelTemplateDetails = EMPTY_OBJECT,
      loading = false,
    },
    ref
  ) => {
    const { getTransitionStates } = useModelInventory();
    // State for edit modal
    const [openModal, setOpenModal] = useState(false);
    const [selectedAttribute, setSelectedAttribute] = useState(null);
    const [attributeValues, setAttributeValues] = useState({});
    const [openChat, setOpenChat] = useState(false);
    const [transitionStates, setTransitionStates] = useState([]);
    const [allChatData, setAllChatData] = useState({
      chats: [],
      attribute: {},
    });
    const [openDerivedModal, setOpenDerivedModal] = useState(false);
    const [overrideData, setOverrideData] = useState([]);

    // Constraint-related state
    const [lookupSourceAttributes, setLookupSourceAttributes] = useState([]);
    const [autoCompleteSourceAttributes, setAutoCompleteSourceAttributes] =
      useState([]);
    const [overrideDropdownOptions, setOverrideDropdownOptions] = useState({});
    const [dateCalendarConstraints, setDateCalendarConstraints] = useState({});
    const [dateCalendarSourceAttributes, setDateCalendarSourceAttributes] =
      useState([]);
    const [editableConstraints, setEditableConstraints] = useState({});
    const [editableSourceAttributes, setEditableSourceAttributes] = useState(
      []
    );
    const [mandatoryRules, setMandatoryRules] = useState({});
    const [mandatorySourceAttributes, setMandatorySourceAttributes] = useState(
      []
    );
    const [isLoadingConstraints, setIsLoadingConstraints] = useState(false);
    const [originalValues, setOriginalValues] = useState({});
    const [constraintCallChain, setConstraintCallChain] = useState(new Set());

    const allAttributes = useMemo(() => {
      const basicAttributes = formDetails?.attributes || [];
      const sectionedAttributes = formDetails?.sections?.reduce(
        (acc, section) => {
          section?.attributes?.forEach((attribute) => acc.push(attribute));
          return acc;
        },
        []
      );
      return [...basicAttributes, ...(sectionedAttributes || [])];
    }, [formDetails]);

    const preliminaryRef = useRef(null);
    const descriptiveRef = useRef(null);
    // State to pass targeted attribute navigation to OtherDetails
    const [descriptiveTargetAttr, setDescriptiveTargetAttr] = useState(null);
    // Ref to track constraint call chain to avoid stale closures
    const constraintCallChainRef = useRef(new Set());
    // Ref to track auto-populate timers for cleanup
    const autoPopulateTimersRef = useRef([]);
    // ref to track edit button disabled prev value
    const prevEditButtonDisableRef = useRef(null);
    // ref to track impact button state prev value
    const prevImpactButtonRef = useRef(null);
    const { aiHighlightStyles, highlightElement } = useAIAttributeHighlight();
    // Keep ref in sync with state
    useEffect(() => {
      constraintCallChainRef.current = constraintCallChain;
    }, [constraintCallChain]);

    const handleOpenDerivedModal = useCallback(
      (attribute) => {
        setSelectedAttribute(attribute);
        setOpenDerivedModal(true);
      },
      [setSelectedAttribute, setOpenDerivedModal]
    );

    const isBasicDetailsPresent = useMemo(() => {
      // Section is present only if hideBasicDetails is false AND there are attributes
      return !hideBasicDetails && formDetails?.attributes?.length > 0;
    }, [formDetails, hideBasicDetails]);

    const { fetchWorkflowGroups } = useWorkflowGroups();
    const { handleGetSectionPermission } = useSectionPermissions();
    const { getConstraintSources, getConstraints } = useAttribute();
    const entityKey = `${entityData?.entity_type}_${entityData?.entity_id}`;
    const sectionPermissions = useSelector(
      (state) => state?.common?.sectionPermissions?.[entityKey]
    );
    const entityCurrentGroupId = useSelector(
      (state) =>
        state?.common?.workflowGroupsVar?.[entityKey]?.entityCurrentGroupId
    );

    const { tenant } = useSelector((state) => state);

    const { pluginConfig } = tenant;

    //for HDFCUAM
    const isHDFCUAMEnabled = useMemo(() => {
      return !!pluginConfig?.HDFCUAM;
    }, [pluginConfig]);

    const allSectionList = useMemo(() => {
      let allSectionData = [];

      // Add direct template attributes (not in sections)
      if (formDetails?.attributes && Array.isArray(formDetails.attributes)) {
        allSectionData.push({
          section_name: preliminarySectionName,
          attributes: formDetails.attributes,
        });
      }

      // Add attributes from all sections
      if (formDetails?.sections && Array.isArray(formDetails.sections)) {
        formDetails.sections.forEach((section) => {
          if (section?.attributes && Array.isArray(section.attributes)) {
            allSectionData.push({
              section_name: section?.section_name,
              attributes: section.attributes,
            });
          }
        });
      }

      return allSectionData;
    }, [formDetails]);

    const filteredSections = useMemo(() => {
      return filterSectionsByPermissions({
        sections: formDetails?.sections,
        sectionPermissions,
        entityCurrentGroupId,
        entitySelectedGroupId,
        selectedStateNode,
        isNodeEdit,
      });
    }, [
      formDetails?.sections,
      sectionPermissions,
      entityCurrentGroupId,
      entitySelectedGroupId,
      selectedStateNode,
      isNodeEdit,
    ]);

    /**
     * Determines the effective editing mode based on workflow group/state selection.
     * Forces 'view' mode when a group or state is selected, unless in group edit modal.
     */
    const modifiedEditingMode = useMemo(() => {
      if (
        !isValueEmpty(entitySelectedGroupId?.groupId) ||
        !isValueEmpty(selectedStateNode)
      ) {
        return isGroupEditModal ? mode : 'view';
      } else {
        return mode;
      }
    }, [mode, entitySelectedGroupId, selectedStateNode, isGroupEditModal]);

    /**
     * this function will return visible attributes
     * conditionally visible attributes across basic att and section att with manage permission only
     */
    const { visibleAttributesList } = useMemo(() => {
      const allFormValues = {
        ...attributeDetails,
        ...syncAttributes,
        ...(entityDetailsFormikForm?.values || {}),
      };

      // map willl ensures uniqueness by attribute id
      const attributeMap = new Map();

      // helper func to collect visible attributes
      const collectVisible = (attributes = EMPTY_ARRAY) => {
        /**
         * on initial render, `allFormValues` will be empty.
         * it is guaranteed that `allFormValues` is never an empty object after initialization,
         * so pass all attributes instead of getting those from getVisibleAttributes
         */
        const sourceAttributes = isValueEmpty(allFormValues)
          ? attributes
          : getVisibleAttributes(attributes, allFormValues)
              ?.visibleAttributes || [];

        sourceAttributes.forEach((attr) => {
          if (attr?.id) {
            attributeMap.set(attr.id, attr);
          }
        });
      };

      // Get visible basic details attributes
      if (Array.isArray(formDetails?.attributes)) {
        collectVisible(formDetails.attributes);
      }

      // Get visible attributes from all filtered sections
      filteredSections?.forEach((section) => {
        /**
         * edit button is disable or not should be defined by section with manage permission
         * so consider section with manage Permission
         * as filteredSections contain section section with View & Manage permission
         */
        const hasManagePermission =
          // When opened from a group edit modal, we want to allow 'manage' permissions by default
          // to ensure all attributes can be edited in the modal.
          isGroupEditModal ||
          sectionPermissions?.section_permissions?.[section?.section_id] ==
            'manage';

        if (hasManagePermission && Array.isArray(section?.attributes)) {
          collectVisible(section.attributes);
        }
      });

      return {
        visibleAttributesList: Array.from(attributeMap.values()),
        visibleAttributeListIds: new Set(attributeMap.keys()),
      };
    }, [
      formDetails,
      filteredSections,
      attributeDetails,
      syncAttributes,
      entityDetailsFormikForm?.values,
      sectionPermissions,
      isGroupEditModal,
    ]);

    // Use error dialog hook
    const {
      openErrorDialog,
      errorDialogData,
      allSectionList: errorDialogSectionList,
      handleCloseErrorDialog,
      validateAndShowErrors,
      closeErrorDialog,
    } = useErrorDialog({
      entityDetailsFormikForm,
      validateFilteredFormikErrors,
      formDetails: { ...formDetails, sections: filteredSections },
      attributeDetails,
      syncAttributes,
      showErrorsOnSave,
      setShowErrorsOnSave,
    });

    useEffect(() => {
      const hasNoVisibleAttributes =
        !Array.isArray(visibleAttributesList) ||
        visibleAttributesList.length === 0;
      /**
       * return true, if every attribute is non editable, an attribute is considered non-editable if:
       * `is_editable` is false,
       * `is_derived` is true
       */
      /**
       * Special case for HDFC UAM plugin
       * If attribute is assigneduser and plugin is enabled, then it is editable only if user is super admin or group admin
       */
      //hasNoVisibleAttributes is true, it means hasNoEditableAttributes is true which means there are no editable attributes present in the form
      const hasNoEditableAttributes = hasNoVisibleAttributes
        ? true
        : visibleAttributesList?.every((att) => {
            if (
              att?.name?.toLowerCase() === 'assigneduser' &&
              pluginConfig?.HDFCUAM
            ) {
              return true;
            }
            return !att?.is_editable || att?.is_derived;
          });

      // this will stop pushing same changes to onEditButtonStateChange callback function
      // if last value is same as now, we will not push chnages into callback
      if (prevEditButtonDisableRef?.current !== hasNoEditableAttributes) {
        prevEditButtonDisableRef.current = hasNoEditableAttributes;
        onEditButtonStateChange?.(hasNoEditableAttributes);
      }

      /**
       * Impact button visibility
       * `hasFieldChangeAttributes` is true when:
       *   - there are visible attributes, AND
       *   - at least one of the visible attributes has data_type === 'FieldChange'
       * When true the parent should show the Impact button.
       */
      const hasFieldChangeAttributes =
        !hasNoVisibleAttributes &&
        visibleAttributesList.some((att) => att?.data_type === 'FieldChange');

      if (prevImpactButtonRef?.current !== hasFieldChangeAttributes) {
        prevImpactButtonRef.current = hasFieldChangeAttributes;
        onImpactButtonStateChange?.(hasFieldChangeAttributes);
      }
    }, [visibleAttributesList, pluginConfig?.HDFCUAM]);

    useImperativeHandle(ref, () => ({
      validateAndShowErrors,
      closeErrorDialog,

      scrollToPreliminary: () => {
        const container = scrollContainerRef?.current;
        const el = preliminaryRef.current;

        if (container && el) {
          container.scrollTo({
            top: el.offsetTop - container.offsetTop - 16,
            behavior: 'smooth',
          });
        }
      },

      scrollToDescriptive: () => {
        const container = scrollContainerRef?.current;
        const el = descriptiveRef.current;

        if (container && el) {
          container.scrollTo({
            top: el.offsetTop - container.offsetTop - 16,
            behavior: 'smooth',
          });
        }
      },

      scrollToDescriptiveSection: (sectionName) => {
        const container = scrollContainerRef?.current;
        const descriptiveEl = descriptiveRef.current;

        if (container && descriptiveEl) {
          container.scrollTo({
            top: descriptiveEl.offsetTop - container.offsetTop - 16,
            behavior: 'smooth',
          });
        }

        if (!sectionName || !descriptiveEl) {
          return;
        }

        setTimeout(() => {
          const normalize = (value) =>
            String(value || '')
              .trim()
              .toLowerCase();
          const targetName = normalize(sectionName);
          const candidates = Array.from(
            descriptiveEl.querySelectorAll(
              '.MuiListItem-root, .MuiListItemButton-root, [role="button"]'
            )
          );

          const target = candidates.find(
            (item) => normalize(item.textContent) === targetName
          );

          if (target) {
            if (typeof target.click === 'function') {
              target.click();
            }
          }
        }, 220);
      },

      scrollToAttribute: (attrId, sectionName) => {
        if (!attrId) {
          return;
        }

        // Priority: check if attribute exists in Preliminary Details
        const basicAttrs = formDetails?.attributes || [];
        const isInPreliminary = basicAttrs.some(
          (attr) => String(attr?.id) === String(attrId)
        );

        if (isInPreliminary) {
          // Scroll to Preliminary section first
          const container = scrollContainerRef?.current;
          const el = preliminaryRef.current;
          if (container && el) {
            container.scrollTo({
              top: el.offsetTop - container.offsetTop,
              behavior: 'smooth',
            });
          }
          // After scroll settles, find and highlight the specific attribute
          setTimeout(() => {
            const attrEl = document.querySelector(
              `[data-attribute-id="${attrId}"]`
            );
            if (attrEl) {
              attrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setTimeout(highlightElement.bind(null, attrEl), 350);
          }, 300);
          return;
        }

        // Descriptive path: scroll to descriptive section, expand accordion, delegate to OtherDetails
        const container = scrollContainerRef?.current;
        const descriptiveEl = descriptiveRef.current;
        if (container && descriptiveEl) {
          container.scrollTo({
            top: descriptiveEl.offsetTop - container.offsetTop,
            behavior: 'smooth',
          });
        }

        // Expand the descriptive accordion if collapsed
        if (descriptiveEl) {
          setTimeout(() => {
            const accordionButton = descriptiveEl.querySelector(
              '.MuiAccordionSummary-root'
            );
            const accordion = accordionButton?.closest('.MuiAccordion-root');
            if (accordion && !accordion.classList.contains('Mui-expanded')) {
              accordionButton.click();
            }
          }, 150);
        }

        // Signal OtherDetails to handle section switch + pagination + highlight
        setDescriptiveTargetAttr({
          attrId: String(attrId),
          sectionName,
          timestamp: Date.now(),
        });
      },
    }));

    const entityId = useMemo(
      () => entityData?.entity_id,
      [entityData?.entity_id]
    );
    const entityType = useMemo(
      () => entityData?.entity_type,
      [entityData?.entity_type]
    );

    // Root level attributes from formDetails
    const basicDetailsAttributes = useMemo(
      () => formDetails?.attributes || [],
      [formDetails]
    );

    const handleGetTransitionStates = () => {
      getTransitionStates({
        entity_type: entityType,
        entity_id: entityId,
      }).then((res) => {
        if (res) {
          setTransitionStates(res?.data);
        }
      });
    };

    useEffect(() => {
      if (
        !isValueEmpty(entityType) &&
        !isValueEmpty(entityId) &&
        openChat &&
        transitionStates.length === 0
      ) {
        handleGetTransitionStates();
      }
    }, [entityType, entityId, openChat]);

    // Handler for opening the edit modal
    const handleEditClick = useCallback(
      (attribute) => {
        setSelectedAttribute(attribute);
        setOpenModal(true);
      },
      [setSelectedAttribute, setOpenModal]
    );

    const handleClose = useCallback(() => {
      setOpenChat(false);
      setAllChatData({ chats: [], attribute: {} });
    }, [setOpenChat, setAllChatData]);

    const handleOpenChat = useCallback(
      (attributeChats, attribute) => {
        setAllChatData({ chats: attributeChats, attribute });
        setOpenChat(true);
      },
      [setOpenChat, setAllChatData]
    );

    const handleGetAttComments = useCallback(
      (params) => {
        getAttComments(params).then((data) => {
          setAllChatData((prev) => {
            if (isValueEmpty(prev.attribute)) {
              return { chats: [], attribute: {} };
            }
            return {
              ...prev,
              chats: data?.[prev.attribute?.name],
            };
          });
        });
      },
      [setAllChatData]
    );

    useEffect(() => {
      if (entityId && entityType) {
        handleGetAttComments({ entity_id: entityId, entity_type: entityType });
      }
    }, [entityId, entityType, handleGetAttComments]);

    // Handler for closing the modal
    const handleCloseModal = useCallback(() => {
      setOpenModal(false);
      setSelectedAttribute(null);
    }, [setSelectedAttribute, setOpenModal]);

    // Handler for saving attribute values
    const handleSave = useCallback(
      (attribute, newValue) => {
        handleUpdateEntity(
          {
            [attribute?.id]: newValue,
          },
          attribute?.attribute_origin
        ).then((res) => {
          if (res) {
            handleGetSectionPermission({ entityId, entityType });
          }
        });
      },
      [handleUpdateEntity, entityId, entityType, handleGetSectionPermission]
    );

    const handleAttributeClick = useCallback(
      (attribute) => {
        handleOpenNoteModal();
        const selectedEntityData = {
          entityType,
          entityId,
          id: attribute?.id,
          name: attribute?.name,
          /**
           * this will be used to trigger the attributes mention and
           * we need to pass this as we are directly adding the attributes into the editor
           * without selection so this will be used to detect which type of trigger used to create this entity
           */
          mentionType: MENTIONED_ENTITIES.attribute.mentionType,
          entityMutability: MENTIONED_ENTITIES.attribute.entityMutability,
        };
        handleSetSelectedEntityToAddInEditor(selectedEntityData);
        handleSetAddNoteModalHeading('Mention attribute in chat');
      },
      [
        handleSetSelectedEntityToAddInEditor,
        handleSetAddNoteModalHeading,
        entityType,
        entityId,
      ]
    );

    useEffect(() => {
      setAttributeValues((prevValues) => ({
        ...prevValues,
        ...attributeDetails,
      }));
    }, [attributeDetails]);

    const getOverrideData = useCallback(() => {
      getOverrideAttributeValue(
        {
          entityType: entityType,
          entityId: entityId,
        },
        false
      ).then((res) => {
        if (res?.data) {
          setOverrideData(res.data);
        }
      });
    }, [entityId, entityType, setOverrideData, getOverrideAttributeValue]);

    useEffect(() => {
      if (entityId && entityType) {
        getOverrideData();
      }
    }, [entityId, entityType]);

    const refreshSingleAttribute = useCallback(
      (attrId, newValue) => {
        //Update local state (drives BasicDetails & OtherDetails UI)
        setAttributeValues((prev) => ({
          ...prev,
          [attrId]: newValue,
        }));

        // Update Formik so form has updated value
        entityDetailsFormikForm?.setFieldValue(attrId, newValue);
      },
      [setAttributeValues, entityDetailsFormikForm]
    );

    useEffect(() => {
      if (
        entityData?.template_id &&
        entityData?.template_version_id &&
        !isValueEmpty(entityData?.status)
      ) {
        fetchWorkflowGroups({
          template_id: entityData?.template_id,
          template_version_id: entityData?.template_version_id,
          entity_current_status: entityData?.status,
          entity_id: entityData?.entity_id,
          entity_type: entityData?.entity_type,
          loader: false,
        });
      }
    }, [
      entityData?.template_id,
      entityData?.template_version_id,
      entityData?.status,
      fetchWorkflowGroups,
    ]);

    // Debounce timer ref for constraint API calls
    const debounceTimerRef = useRef(null);

    // validate constraint values against new options
    const validateConstraintValues = useCallback(
      (newOverrideOptions) => {
        // Iterate through each attribute that has new constraint options
        Object.keys(newOverrideOptions).forEach((attrId) => {
          const currentValue = entityDetailsFormikForm?.values?.[attrId];
          const newOptions = newOverrideOptions[attrId];

          // Only validate if there's a current value and new options are provided
          if (
            !isValueEmpty(currentValue) &&
            newOptions &&
            Array.isArray(newOptions)
          ) {
            // Case 1: No valid options exist - clear the field value
            if (newOptions.length === 0) {
              entityDetailsFormikForm?.setFieldValue(attrId, null);
              if (handleAutoSave) {
                handleAutoSave({ [attrId]: null });
              }
              return;
            }

            // Extract valid option values, handling both object and primitive formats
            const validOptionValues = newOptions.map((option) =>
              typeof option === 'object'
                ? option?.value || option?.entity_id
                : option
            );

            // Case 2: Multi-select field - remove invalid selections, keep valid ones
            if (Array.isArray(currentValue)) {
              // Filter out values that are no longer in the valid options list
              const invalidValues = currentValue?.filter(
                (val) =>
                  !validOptionValues.includes(
                    val?.value || val?.entity_id || val
                  )
              );

              // Only update if there are invalid values to remove
              if (invalidValues.length > 0) {
                // Keep only the valid selections
                const validValues = currentValue.filter((val) =>
                  validOptionValues.includes(
                    val?.value || val?.entity_id || val
                  )
                );

                // Set to valid values array or null if no valid selections remain
                const correctValue =
                  validValues.length > 0 ? validValues : null;

                entityDetailsFormikForm?.setFieldValue(attrId, correctValue);

                if (handleAutoSave) {
                  handleAutoSave({ [attrId]: correctValue });
                }
              }
            } else {
              // Case 3: Single-select field - clear if current value is invalid
              // Check validity for both object format (currentValue.value) and primitive format
              const isCurrentValueValid = validOptionValues.includes(
                currentValue?.value || currentValue?.entity_id || currentValue
              );

              // Clear the field if current selection is no longer valid
              if (!isCurrentValueValid) {
                entityDetailsFormikForm?.setFieldValue(attrId, null);

                if (handleAutoSave) {
                  handleAutoSave({ [attrId]: null });
                }
              }
            }
          }
        });
      },
      [entityDetailsFormikForm, handleAutoSave]
    );

    // Debounced constraint API call for value changes
    const handleConstraintValueChange = useCallback(
      async (
        sourceAttributeId,
        newValue,
        oldValue,
        isAutoPopulated = false
      ) => {
        // Store original value for error handling
        setOriginalValues((prev) => ({
          ...prev,
          [sourceAttributeId]: oldValue,
        }));

        // Clear existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set new debounced timer
        debounceTimerRef.current = setTimeout(async () => {
          try {
            setIsLoadingConstraints(true);
            const allFormValues = {
              ...attributeDetails,
              ...syncAttributes,
              ...(entityDetailsFormikForm?.values || {}),
              [sourceAttributeId]: newValue,
            };

            const response = await getConstraints({
              entity_id: entityId,
              entity_type: entityData?.entity_type,
              source_attribute_id: sourceAttributeId,
              form_data: allFormValues,
            });

            if (response?.data?.lookup) {
              const newOverrideOptions = response.data.lookup;

              // Update override options
              setOverrideDropdownOptions((prev) => ({
                ...prev,
                ...newOverrideOptions,
              }));

              // Validate constraint values
              validateConstraintValues(newOverrideOptions);
            }

            // Handle auto population from constraint API response
            if (response?.data?.autopupulate) {
              // eslint-disable-next-line no-use-before-define
              handleAutoPopulation &&
                // eslint-disable-next-line no-use-before-define
                handleAutoPopulation(response.data.autopupulate);
            }

            // Clear constraint call chain after processing response
            if (isAutoPopulated) {
              const attrIdNum = Number.parseInt(sourceAttributeId);
              setConstraintCallChain((prev) => {
                const newChain = new Set(prev);
                newChain.delete(attrIdNum);
                return newChain;
              });
              constraintCallChainRef.current.delete(attrIdNum);
            }

            // Handle date calendar constraints
            if (response?.data?.date_calander) {
              const newDateCalendarConstraints = response.data.date_calander;

              // Update date calendar constraints
              setDateCalendarConstraints((prev) => ({
                ...prev,
                ...newDateCalendarConstraints,
              }));

              // Dynamically update Yup form schema with new date constraints
              if (setFormSchema) {
                setFormSchema((prevSchema) => {
                  if (!prevSchema) {
                    return prevSchema;
                  }
                  return applyDateCalendarConstraints(
                    prevSchema,
                    newDateCalendarConstraints
                  );
                });
              }
            }

            // Handle editable constraints
            if (response?.data?.editable) {
              const newEditableConstraints = response.data.editable;

              setEditableConstraints((prev) => ({
                ...prev,
                ...newEditableConstraints,
              }));
            }

            // Handle mandatory constraints
            if (response?.data?.mandatory) {
              const newMandatoryConstraints = response.data.mandatory;

              setMandatoryRules((prev) => ({
                ...prev,
                ...newMandatoryConstraints,
              }));

              // Dynamically update Yup form schema with new mandatory constraints
              if (setFormSchema) {
                setFormSchema((prevSchema) => {
                  if (!prevSchema) {
                    return prevSchema;
                  }
                  return applyMandatoryConstraints(
                    prevSchema,
                    newMandatoryConstraints,
                    allAttributes
                  );
                });
              }
            }
          } catch (error) {
            console.error(
              'Error fetching constraints for value change:',
              error
            );

            // Revert to original value on error
            const originalValue = originalValues[sourceAttributeId];
            if (originalValue !== undefined) {
              entityDetailsFormikForm?.setFieldValue(
                sourceAttributeId,
                originalValue
              );
            }

            // Clear constraint call chain on error
            if (isAutoPopulated) {
              const attrIdNum = Number.parseInt(sourceAttributeId);
              setConstraintCallChain((prev) => {
                const newChain = new Set(prev);
                newChain.delete(attrIdNum);
                return newChain;
              });
              constraintCallChainRef.current.delete(attrIdNum);
            }
          } finally {
            setIsLoadingConstraints(false);
          }
        }, 500); // 500ms debounce
      },
      [
        entityId,
        entityData?.entity_type,
        attributeDetails,
        syncAttributes,
        entityDetailsFormikForm,
        getConstraints,
        handleAutoSave,
        originalValues,
        validateConstraintValues,
        setFormSchema,
        applyDateCalendarConstraints,
        applyMandatoryConstraints,
      ]
    );

    // Helper function to handle auto population
    const handleAutoPopulation = useCallback(
      (autoPopulateData) => {
        Object.keys(autoPopulateData || {}).forEach((attrId) => {
          const autoPopulatedValue = autoPopulateData[attrId];
          const currentValue = entityDetailsFormikForm?.values?.[attrId];

          // Only auto populate if the new value is not empty/null, or if current value is empty
          if (!isValueEmpty(autoPopulatedValue) || isValueEmpty(currentValue)) {
            const valueToSet = !isValueEmpty(autoPopulatedValue)
              ? autoPopulatedValue
              : currentValue;

            // Skip if value hasn't actually changed to prevent unnecessary API calls
            if (currentValue === valueToSet) {
              return;
            }

            // Update both Formik and local state without triggering change handlers
            entityDetailsFormikForm?.setFieldValue(attrId, valueToSet);
            setAttributeValues((prev) => ({
              ...prev,
              [attrId]: valueToSet,
            }));

            const attrIdNum = Number.parseInt(attrId);
            // Check if auto populated attribute is a constraint source and trigger API call
            // Use ref to get latest value and avoid stale closure issues
            if (
              autoCompleteSourceAttributes.includes(attrIdNum) &&
              !constraintCallChainRef.current.has(attrIdNum)
            ) {
              // Add to call chain to prevent cycles
              setConstraintCallChain((prev) => {
                const newSet = new Set(prev);
                newSet.add(attrIdNum);
                return newSet;
              });
              constraintCallChainRef.current.add(attrIdNum);

              // Trigger constraint API call for this auto populated value
              const timerId = setTimeout(() => {
                handleConstraintValueChange(
                  attrId,
                  valueToSet,
                  currentValue,
                  true
                );
              }, 100);

              // Track timer for cleanup
              autoPopulateTimersRef.current.push(timerId);
            }
          }
        });
      },
      [
        entityDetailsFormikForm,
        setAttributeValues,
        autoCompleteSourceAttributes,
        handleConstraintValueChange,
      ]
    );

    // Fetch constraint sources on edit mode entry
    const fetchConstraintSources = useCallback(async () => {
      if (!entityId || !entityData?.entity_type) return;

      try {
        const response = await getConstraintSources(
          {
            entity_id: entityId,
            entity_type: entityData?.entity_type,
          },
          false
        );

        if (response?.data?.lookup_source_attributes) {
          setLookupSourceAttributes(response.data.lookup_source_attributes);
        }
        if (response?.data?.autocomplete_source_attributes) {
          setAutoCompleteSourceAttributes(
            response.data.autocomplete_source_attributes
          );
        }
        if (response?.data?.date_calander_attributes) {
          setDateCalendarSourceAttributes(
            response.data.date_calander_attributes
          );
        }
        if (response?.data?.editable_source_attributes) {
          setEditableSourceAttributes(response.data.editable_source_attributes);
        }
        if (response?.data?.mandatory_source_attributes) {
          setMandatorySourceAttributes(
            response.data.mandatory_source_attributes
          );
        }
      } catch (error) {
        console.error('Error fetching constraint sources:', error);
      }
    }, [entityId, entityData?.entity_type, getConstraintSources]);

    // Fetch initial constraints when edit mode starts
    const fetchInitialConstraints = useCallback(async () => {
      if (!entityId || !entityData?.entity_type || !isMultipleEditMode) return;

      try {
        setIsLoadingConstraints(true);
        const allFormValues = {
          ...attributeDetails,
          ...syncAttributes,
          ...(entityDetailsFormikForm?.values || {}),
        };

        const response = await getConstraints({
          entity_id: entityId,
          entity_type: entityData?.entity_type,
          form_data: allFormValues,
        });

        if (response?.data?.lookup) {
          const newOverrideOptions = response.data.lookup;
          setOverrideDropdownOptions(newOverrideOptions);
          validateConstraintValues(newOverrideOptions);
        }

        // Handle autopopulation from initial constraints
        if (response?.data?.autopupulate) {
          handleAutoPopulation(response.data.autopupulate);
        }
        // Handle date calendar constraints on initial load
        if (response?.data?.date_calander) {
          const newDateCalendarConstraints = response.data.date_calander;

          // Update date calendar constraints
          setDateCalendarConstraints((prev) => ({
            ...prev,
            ...newDateCalendarConstraints,
          }));

          // Dynamically update Yup form schema with new date constraints
          if (setFormSchema) {
            setFormSchema((prevSchema) => {
              if (!prevSchema) {
                return prevSchema;
              }
              return applyDateCalendarConstraints(
                prevSchema,
                newDateCalendarConstraints
              );
            });
          }
        }
        // Handle editable constraints on initial load
        if (response?.data?.editable) {
          const newEditableConstraints = response.data.editable;

          setEditableConstraints((prev) => ({
            ...prev,
            ...newEditableConstraints,
          }));
        }
        // Handle mandatory constraints on initial load
        if (response?.data?.mandatory) {
          const newMandatoryConstraints = response.data.mandatory;

          setMandatoryRules((prev) => ({
            ...prev,
            ...newMandatoryConstraints,
          }));

          // Dynamically update Yup form schema with new mandatory constraints
          if (setFormSchema) {
            setFormSchema((prevSchema) => {
              if (!prevSchema) {
                return prevSchema;
              }
              return applyMandatoryConstraints(
                prevSchema,
                newMandatoryConstraints,
                allAttributes
              );
            });
          }
        }
      } catch (error) {
        console.error('Error fetching initial constraints:', error);
      } finally {
        setIsLoadingConstraints(false);
      }
    }, [
      entityId,
      entityData?.entity_type,
      attributeDetails,
      syncAttributes,
      entityDetailsFormikForm?.values,
      isMultipleEditMode,
      getConstraints,
      handleAutoPopulation,
      validateConstraintValues,
      setFormSchema,
      applyDateCalendarConstraints,
      applyMandatoryConstraints,
    ]);

    // Hook to detect when constraint source attributes change
    const handleAttributeValueChange = useCallback(
      (attributeId, newValue, oldValue) => {
        // Check if this attribute is a lookup source attribute
        if (
          lookupSourceAttributes.includes(Number.parseInt(attributeId)) ||
          autoCompleteSourceAttributes.includes(Number.parseInt(attributeId)) ||
          dateCalendarSourceAttributes.includes(Number.parseInt(attributeId)) ||
          editableSourceAttributes.includes(Number.parseInt(attributeId)) ||
          mandatorySourceAttributes.includes(Number.parseInt(attributeId))
        ) {
          handleConstraintValueChange(attributeId, newValue, oldValue);
        }
      },
      [
        lookupSourceAttributes,
        autoCompleteSourceAttributes,
        dateCalendarSourceAttributes,
        editableSourceAttributes,
        mandatorySourceAttributes,
        handleConstraintValueChange,
      ]
    );

    // Fetch constraint sources when edit mode starts
    useEffect(() => {
      if (isMultipleEditMode && entityId && entityData?.entity_type) {
        fetchConstraintSources();
      }
    }, [isMultipleEditMode, entityId, entityData?.entity_type]);

    // Fetch initial constraints after constraint sources are loaded
    useEffect(() => {
      if (
        lookupSourceAttributes.length > 0 ||
        autoCompleteSourceAttributes.length > 0 ||
        dateCalendarSourceAttributes.length > 0 ||
        editableSourceAttributes.length > 0 ||
        mandatorySourceAttributes.length > 0
      ) {
        fetchInitialConstraints();
      }
    }, [
      lookupSourceAttributes,
      autoCompleteSourceAttributes,
      dateCalendarSourceAttributes,
      editableSourceAttributes,
      mandatorySourceAttributes,
    ]);
    // Cleanup debounce timer and auto-populate timers
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        // Clear all auto-populate timers
        autoPopulateTimersRef.current.forEach((timerId) => {
          clearTimeout(timerId);
        });
        autoPopulateTimersRef.current = [];
        // Reset constraint call chain ref
        constraintCallChainRef.current = new Set();
      };
    }, []);

    if (loading && allAttributes?.length === 0) {
      return <FormSkeleton />;
    }

    return (
      <Grid container xs={12} gap={2}>
        {aiHighlightStyles}
        {/* Basic details */}
        <Box ref={preliminaryRef} width={'100%'}>
          {isBasicDetailsPresent ? (
            <Grid item container xs={12} gap={2}>
              <SectionAccordion title={preliminarySectionName}>
                <BasicDetails
                  attributes={basicDetailsAttributes}
                  attributeValues={attributeValues}
                  onEditAttribute={handleEditClick}
                  mode={modifiedEditingMode}
                  hasDraftChanges={hasDraftChanges}
                  entityDetailsFormikForm={entityDetailsFormikForm}
                  isMultipleEditMode={isMultipleEditMode}
                  usersList={usersList}
                  teamUsersList={teamUsersList}
                  groupList={groupList}
                  isUserAuthorizedForUpdateAttribute={
                    isUserAuthorizedForUpdateAttribute
                  }
                  allAttributeChats={allAttributeChats}
                  handleOpenChat={handleOpenChat}
                  handleAttributeClick={handleAttributeClick}
                  entityData={entityData}
                  isUserAuthorizedToOverrideAttribute={
                    isUserAuthorizedToOverrideAttribute
                  }
                  overrideData={overrideData}
                  refreshSingleAttribute={refreshSingleAttribute}
                  handleAutoSave={handleAutoSave}
                  getOverrideData={getOverrideData}
                  getAttributeDetails={getAttributeDetails}
                  updateInitialValuesFromAPI={updateInitialValuesFromAPI}
                  allSectionList={allSectionList}
                  // Constraint-related props
                  lookupSourceAttributes={lookupSourceAttributes}
                  autoCompleteSourceAttributes={autoCompleteSourceAttributes}
                  overrideDropdownOptions={overrideDropdownOptions}
                  dateCalendarConstraints={dateCalendarConstraints}
                  dateCalendarSourceAttributes={dateCalendarSourceAttributes}
                  editableSourceAttributes={editableSourceAttributes}
                  mandatoryConstraints={mandatoryRules}
                  mandatorySourceAttributes={mandatorySourceAttributes}
                  isLoadingConstraints={isLoadingConstraints}
                  handleAttributeValueChange={handleAttributeValueChange}
                  formDetails={formDetails}
                  isHDFCUAMEnabled={isHDFCUAMEnabled}
                  currentModelStatus={currentModelStatus}
                  groupDetails={groupDetails}
                  groupListForTeamMembers={groupListForTeamMembers}
                  getNextPossibleStates={getNextPossibleStates}
                  getModelDetails={getModelDetails}
                  handleGetAttributesData={handleGetAttributesData}
                  clearDraftValues={clearDraftValues}
                  possibleRoles={possibleRoles}
                  getCustomTeamDetails={getCustomTeamDetails}
                  // AI Autofill
                  aiSuggestions={aiSuggestions}
                  onAcceptAISuggestion={onAcceptAISuggestion}
                  onRejectAISuggestion={onRejectAISuggestion}
                  onUndoAISuggestion={onUndoAISuggestion}
                  getSuggestionForAttribute={getSuggestionForAttribute}
                  editableConstraints={editableConstraints}
                  associationTypes={associationTypes}
                  modelTemplateDetails={modelTemplateDetails}
                />
              </SectionAccordion>
            </Grid>
          ) : null}
        </Box>

        {/* Sections Details */}
        <Box ref={descriptiveRef} width={'100%'}>
          <Grid item container xs={12}>
            {filteredSections?.length > 0 ? (
              <SectionAccordion title={descriptiveTitle} defaultExpanded>
                <OtherDetails
                  groupDetails={groupDetails}
                  isGroupEditModal={isGroupEditModal}
                  accordionView
                  sectionsData={filteredSections}
                  sectionPermissions={sectionPermissions}
                  hasDraftChanges={hasDraftChanges}
                  attributeValues={attributeValues}
                  handleUpdateEntity={handleUpdateEntity}
                  mode={modifiedEditingMode}
                  entityData={entityData}
                  entityDetailsFormikForm={entityDetailsFormikForm}
                  isMultipleEditMode={isMultipleEditMode}
                  usersList={usersList}
                  teamUsersList={teamUsersList}
                  groupList={groupList}
                  isUserAuthorizedForUpdateAttribute={
                    isUserAuthorizedForUpdateAttribute
                  }
                  allAttributeChats={allAttributeChats}
                  handleOpenChat={handleOpenChat}
                  handleAttributeClick={handleAttributeClick}
                  syncAttributes={syncAttributes}
                  isTableView={isTableView}
                  handleGetSectionPermission={handleGetSectionPermission}
                  isBasicDetailsEmpty={!isBasicDetailsPresent}
                  onCalculateAttribute={handleOpenDerivedModal}
                  isUserAuthorizedToOverrideAttribute={
                    isUserAuthorizedToOverrideAttribute
                  }
                  overrideData={overrideData}
                  refreshSingleAttribute={refreshSingleAttribute}
                  handleAutoSave={handleAutoSave}
                  getOverrideData={getOverrideData}
                  getAttributeDetails={getAttributeDetails}
                  updateInitialValuesFromAPI={updateInitialValuesFromAPI}
                  allSectionList={allSectionList}
                  // Constraint-related props
                  lookupSourceAttributes={lookupSourceAttributes}
                  autoCompleteSourceAttributes={autoCompleteSourceAttributes}
                  overrideDropdownOptions={overrideDropdownOptions}
                  dateCalendarConstraints={dateCalendarConstraints}
                  dateCalendarSourceAttributes={dateCalendarSourceAttributes}
                  editableConstraints={editableConstraints}
                  editableSourceAttributes={editableSourceAttributes}
                  isLoadingConstraints={isLoadingConstraints}
                  handleAttributeValueChange={handleAttributeValueChange}
                  formDetails={formDetails}
                  isHDFCUAMEnabled={isHDFCUAMEnabled}
                  currentModelStatus={currentModelStatus}
                  handleClearSelectedState={handleClearSelectedState}
                  selectedStateNode={selectedStateNode}
                  entitySelectedGroupId={entitySelectedGroupId}
                  setSelectedStateNode={setSelectedStateNode}
                  setEntitySelectedGroupId={setEntitySelectedGroupId}
                  descriptiveTitle={descriptiveTitle}
                  groupListForTeamMembers={groupListForTeamMembers}
                  getNextPossibleStates={getNextPossibleStates}
                  getModelDetails={getModelDetails}
                  handleGetAttributesData={handleGetAttributesData}
                  clearDraftValues={clearDraftValues}
                  possibleRoles={possibleRoles}
                  getCustomTeamDetails={getCustomTeamDetails}
                  // AI Autofill
                  aiSuggestions={aiSuggestions}
                  onAcceptAISuggestion={onAcceptAISuggestion}
                  onRejectAISuggestion={onRejectAISuggestion}
                  onUndoAISuggestion={onUndoAISuggestion}
                  getSuggestionForAttribute={getSuggestionForAttribute}
                  // AI Attribute Navigation
                  targetAttribute={descriptiveTargetAttr}
                  mandatoryConstraints={mandatoryRules}
                  mandatorySourceAttributes={mandatorySourceAttributes}
                  associationTypes={associationTypes}
                  modelTemplateDetails={modelTemplateDetails}
                />
              </SectionAccordion>
            ) : null}
          </Grid>
        </Box>

        {/* Edit Modal */}
        {selectedAttribute && (
          <EditValueModal
            open={openModal}
            handleClose={handleCloseModal}
            attribute={selectedAttribute}
            value={attributeValues[selectedAttribute?.id] || ''}
            handleSave={handleSave}
            usersList={usersList}
            teamUsersList={teamUsersList}
            groupList={groupList}
            entityData={entityData}
            onCalculateAttribute={handleOpenDerivedModal}
            formDetails={formDetails}
            attributeValues={attributeValues}
            isHDFCUAMEnabled={isHDFCUAMEnabled}
            currentModelStatus={currentModelStatus}
            groupDetails={groupDetails}
            modelTemplateDetails={modelTemplateDetails}
          />
        )}
        <ChatBoxPanel
          open={openChat}
          handleDrawerClose={handleClose}
          transitionStates={transitionStates}
          chatData={allChatData?.chats}
          attribute={allChatData?.attribute}
          entityType={entityType}
          entityId={entityId}
          editNote={editNote}
          deleteNote={deleteNote}
          getAllComments={handleGetAttComments}
          isUserAuthorizedForAddNote={true}
          isUserAuthorizedForDeleteNote={true}
          isUserAuthorizedForUpdateNote={true}
        />

        {/* Global Error Dialog for Attribute Validation */}
        {openErrorDialog && !isFromModelOrArtifactsPage && (
          <DraggableErrorSectionDialog
            open={openErrorDialog && !isFromModelOrArtifactsPage}
            onClose={handleCloseErrorDialog}
            errors={errorDialogData}
            sectionData={errorDialogSectionList}
            title="Please fill out the following mandatory field(s)."
          />
        )}
        {openDerivedModal && (
          <DerivedAttributeOverrideModal
            open={openDerivedModal}
            attributes={basicDetailsAttributes}
            attributeValues={attributeValues}
            onCalculateAttribute={handleOpenDerivedModal}
            mode={modifiedEditingMode}
            entityDetailsFormikForm={entityDetailsFormikForm}
            isMultipleEditMode={isMultipleEditMode}
            usersList={usersList}
            teamUsersList={teamUsersList}
            groupList={groupList}
            isUserAuthorizedForUpdateAttribute={
              isUserAuthorizedForUpdateAttribute
            }
            allAttributeChats={allAttributeChats}
            handleOpenChat={handleOpenChat}
            handleAttributeClick={handleAttributeClick}
            entityData={entityData}
            attribute={selectedAttribute}
            isUserAuthorizedToOverrideAttribute={
              isUserAuthorizedToOverrideAttribute
            }
            refreshSingleAttribute={refreshSingleAttribute}
            getOverrideData={getOverrideData}
            getAttributeDetails={getAttributeDetails}
            updateInitialValuesFromAPI={updateInitialValuesFromAPI}
            handleAutoSave={handleAutoSave}
            allSectionList={allSectionList}
            handleAttributeValueChange={handleAttributeValueChange}
          />
        )}
      </Grid>
    );
  }
);

Overview.propTypes = {
  formDetails: PropTypes.oneOfType([PropTypes.object]).isRequired,
  attributeDetails: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  sectionPermissions: PropTypes.oneOfType([PropTypes.object]).isRequired,
  handleUpdateEntity: PropTypes.func.isRequired,
  hasDraftChanges: PropTypes.bool,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  mode: PropTypes.oneOf(['edit', 'view']),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
    template_id: PropTypes.string,
    template_version_id: PropTypes.string,
    status: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    initialValues: PropTypes.shape({}),
    enableReinitialize: PropTypes.bool,
    validationSchema: PropTypes.shape({}),
    onSubmit: PropTypes.func,
    errors: PropTypes.shape({}),
    setFieldValue: PropTypes.func,
    values: PropTypes.shape({}),
    validateForm: PropTypes.func,
    setTouched: PropTypes.func,
    setErrors: PropTypes.objectOf(PropTypes.string),
  }),
  isMultipleEditMode: PropTypes.bool,
  isTableView: PropTypes.bool,
  isUserAuthorizedForUpdateAttribute: PropTypes.bool.isRequired,
  getAttComments: PropTypes.func,
  editNote: PropTypes.func,
  deleteNote: PropTypes.func,
  handleOpenNoteModal: PropTypes.func,
  handleSetAddNoteModalHeading: PropTypes.func,
  handleSetSelectedEntityToAddInEditor: PropTypes.func,
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)]),
  syncAttributes: PropTypes.oneOfType([PropTypes.object]).isRequired,
  loading: PropTypes.bool.isRequired,
  isUserAuthorizedToOverrideAttribute: PropTypes.bool.isRequired,
  getOverrideAttributeValue: PropTypes.func,
  handleAutoSave: PropTypes.func,
  hideBasicDetails: PropTypes.bool.isRequired,
  getAttributeDetails: PropTypes.func,
  updateInitialValuesFromAPI: PropTypes.func.isRequired,
  validateFilteredFormikErrors: PropTypes.func.isRequired,
  showErrorsOnSave: PropTypes.bool,
  setShowErrorsOnSave: PropTypes.func,
  onEditButtonStateChange: PropTypes.func,
  onImpactButtonStateChange: PropTypes.func,
  currentModelStatus: PropTypes.shape({
    currentState: PropTypes.string,
  }),
  entitySelectedGroupId: PropTypes.string,
  selectedStateNode: PropTypes.string,
  handleClearSelectedState: PropTypes.func,
  setSelectedStateNode: PropTypes.func,
  setEntitySelectedGroupId: PropTypes.func,
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  isFromModelOrArtifactsPage: PropTypes.bool,
  setFormSchema: PropTypes.func,
  isGroupEditModal: PropTypes.bool,
  isNodeEdit: PropTypes.bool,
  scrollContainerRef: PropTypes.oneOfType([PropTypes.object]),
  descriptiveTitle: PropTypes.string,
  preliminarySectionName: PropTypes.string,
  groupListForTeamMembers: PropTypes.arrayOf(PropTypes.shape({})),
  getNextPossibleStates: PropTypes.func,
  getModelDetails: PropTypes.func,
  handleGetAttributesData: PropTypes.func,
  clearDraftValues: PropTypes.func,
  possibleRoles: PropTypes.arrayOf(PropTypes.string),
  getCustomTeamDetails: PropTypes.func,
  associationTypes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        entity_name: PropTypes.string,
        entity_type: PropTypes.string,
        template_id: PropTypes.string,
        version_id: PropTypes.string,
      })
    )
  ),
  // AI Autofill
  aiSuggestions: PropTypes.objectOf(PropTypes.shape({})),
  onAcceptAISuggestion: PropTypes.func,
  onRejectAISuggestion: PropTypes.func,
  onUndoAISuggestion: PropTypes.func,
  getSuggestionForAttribute: PropTypes.func,
  modelTemplateDetails: PropTypes.shape({}),
};

export default Overview;
